import { jsx as _jsx } from "react/jsx-runtime";
/* -------------------------------------------------------------------------------------------------
 * PreviewSearch
 * ----------------------------------------------------------------------------------------------- */
import * as React from 'react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { createCollection } from '@radix-ui/react-collection';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { createContextScope } from '@radix-ui/react-context';
import * as Popover from '@radix-ui/react-popover';
import { Primitive } from '@radix-ui/react-primitive';
import { FilterEqual } from '@sitecore-search/data';
import { useWidgetSubQuery } from '@sitecore-search/react';
const preventDefault = (e) => e.preventDefault();
/* -------------------------------------------------------------------------------------------------
 * PreviewSearch
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_NAME = 'PreviewSearch';
const [FocusGroupCollection, useFocusGroupCollection, createFocusGroupCollectionScope] = createCollection(PREVIEW_SEARCH_NAME);
const [createPreviewSearchContext, createPreviewSearchScope] = createContextScope(PREVIEW_SEARCH_NAME, [
    createFocusGroupCollectionScope,
]);
const [ResultsProvider, useResultsContext] = createPreviewSearchContext(PREVIEW_SEARCH_NAME);
const [PreviewSearchProvider, usePreviewSearchContext] = createPreviewSearchContext(PREVIEW_SEARCH_NAME);
const PreviewSearch = (props) => {
    const { __scopePreviewSearch, children, dir = 'ltr', activeItem, defaultActiveItem, onActiveItemChange, ...rootProps } = props;
    const [open, setOpen] = React.useState(false);
    return (_jsx(PreviewSearchProvider, { scope: __scopePreviewSearch, open: open, setOpen: (value) => setOpen(value), dir: dir, children: _jsx(ResultsProviderImpl, { scope: __scopePreviewSearch, activeItem: activeItem, defaultActiveItem: defaultActiveItem, onActiveItemChange: onActiveItemChange, children: _jsx(Popover.Root, { ...rootProps, open: open, onOpenChange: (open) => {
                    setOpen(open);
                }, children: children }) }) }));
};
PreviewSearch.displayName = PREVIEW_SEARCH_NAME;
const ResultsProviderImpl = (props) => {
    const { scope, children } = props;
    const [activeItem, setActiveItem] = React.useState(null);
    const pointerLeaveTimerRef = React.useRef(0);
    React.useEffect(() => {
        return () => {
            window.clearTimeout(pointerLeaveTimerRef.current);
        };
    }, []);
    const resetActiveItem = React.useCallback(() => {
        window.clearTimeout(pointerLeaveTimerRef.current);
        pointerLeaveTimerRef.current = window.setTimeout(() => {
            setActiveItem(null);
        }, 200);
    }, []);
    return (_jsx(ResultsProvider, { scope: scope, activeItem: activeItem, onTriggerEnter: React.useCallback((item) => {
            window.clearTimeout(pointerLeaveTimerRef.current);
            setActiveItem(item);
        }, []), onTriggerLeave: resetActiveItem, onItemsEnter: React.useCallback(() => {
            window.clearTimeout(pointerLeaveTimerRef.current);
        }, []), onItemsLeave: resetActiveItem, children: children }));
};
/* ----------------------------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchInput
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_INPUT_NAME = 'PreviewSearchInput';
const PreviewSearchInput = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, ...inputProps } = props;
    const context = usePreviewSearchContext(PREVIEW_SEARCH_INPUT_NAME, __scopePreviewSearch);
    return (_jsx(Popover.Trigger, { asChild: true, children: _jsx(Primitive.input, { type: "text", role: "searchbox", "aria-expanded": undefined, ...(context.open ? { tabIndex: 0 } : {}), ...inputProps, onClick: composeEventHandlers(props.onClick, (event) => {
                event.preventDefault();
            }), onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
                if (event.key === 'Enter') {
                    if (!context.open) {
                        context.setOpen(true);
                    }
                    else {
                        context.setOpen(false);
                    }
                }
            }), ref: forwardedRef, onChange: composeEventHandlers(props.onChange, () => {
                if (!context.open) {
                    context.setOpen(true);
                }
            }), onFocus: composeEventHandlers(props.onFocus, () => {
                context.setOpen(true);
            }) }) }));
});
PreviewSearchInput.displayName = PREVIEW_SEARCH_INPUT_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchContent
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_CONTENT_NAME = 'PreviewSearchContent';
const PreviewSearchContent = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, children, 
    // PopoverContent props
    sideOffset = 1, ...popoverContentProps } = props;
    return (_jsx(FocusGroupCollection.Provider, { scope: __scopePreviewSearch, children: _jsx(FocusGroupCollection.Slot, { scope: __scopePreviewSearch, children: _jsx(Popover.Content, { sideOffset: sideOffset, side: "bottom", align: "start", avoidCollisions: false, ...popoverContentProps, ref: forwardedRef, onOpenAutoFocus: preventDefault, children: children }) }) }));
});
PreviewSearchContent.displayName = PREVIEW_SEARCH_CONTENT_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchResults
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_RESULTS = 'PreviewSearchResults';
const PreviewSearchResults = (props) => {
    const { __scopePreviewSearch, children, defaultQueryResult: defaultQueryResultProp, ...resultsProps } = props;
    const resultsContext = useResultsContext(PREVIEW_SEARCH_RESULTS, __scopePreviewSearch);
    if (resultsContext.activeItem) {
        return (_jsx(PreviewSearchSuggestionResults, { __scopePreviewSearch: __scopePreviewSearch, ...resultsProps, children: children }, resultsContext.activeItem.id));
    }
    const defaultQueryResult = defaultQueryResultProp || {};
    return typeof children === 'function' ? children(defaultQueryResult) : null;
};
PreviewSearchResults.displayName = PREVIEW_SEARCH_RESULTS;
/* ------------------------------------------------------------------------------------------------- */
const onUpdate = (query, { value, filterAttribute, }) => {
    query.getRequest().resetSearchSuggestion();
    if (filterAttribute) {
        query.getRequest().setSearchFilter(new FilterEqual(filterAttribute, value));
    }
    else {
        query.getRequest().setSearchQueryKeyphrase(value);
    }
};
const PreviewSearchSuggestionResults = (props) => {
    const { __scopePreviewSearch, children } = props;
    const resultsContext = useResultsContext(PREVIEW_SEARCH_RESULTS, __scopePreviewSearch);
    const { id, ...options } = resultsContext.activeItem;
    const queryResult = useWidgetSubQuery({ active: true, onUpdate, ...options }).queryResult;
    return typeof children === 'function' ? children(queryResult) : null;
};
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchItems
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_ITEMS_NAME = 'PreviewSearchItems';
const PreviewSearchItems = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, ...itemsProps } = props;
    const resultsContext = useResultsContext(PREVIEW_SEARCH_RESULTS, __scopePreviewSearch);
    return (_jsx(Primitive.div, { role: "list", ...itemsProps, ref: forwardedRef, onPointerEnter: composeEventHandlers(props.onPointerEnter, whenMouse(() => {
            resultsContext.onItemsEnter();
        })), onPointerLeave: composeEventHandlers(props.onPointerLeave, whenMouse(() => {
            resultsContext.onItemsLeave();
        })) }));
});
PreviewSearchItems.displayName = PREVIEW_SEARCH_ITEMS_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchItem
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_ITEM_NAME = 'PreviewSearchItem';
const PreviewSearchItem = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, ...itemProps } = props;
    return (_jsx(FocusGroupItem, { asChild: true, children: _jsx(Primitive.div, { role: "listitem", ...itemProps, ref: forwardedRef }) }));
});
PreviewSearchItem.displayName = PREVIEW_SEARCH_ITEM_NAME;
const PREVIEW_SEARCH_ITEM_LINK_NAME = 'PreviewSearchItemLink';
const PreviewSearchItemLink = React.forwardRef((props, forwardedRef) => {
    const ref = React.useRef(null);
    const composedRef = useComposedRefs(forwardedRef, ref);
    const { __scopePreviewSearch, ...itemProps } = props;
    const { setOpen } = usePreviewSearchContext(PREVIEW_SEARCH_ITEM_LINK_NAME, __scopePreviewSearch);
    return (_jsx(Primitive.a, { ...itemProps, ref: composedRef, onClick: composeEventHandlers(props.onClick, (e) => {
            e.preventDefault();
            setOpen(false);
        }) }));
});
PreviewSearchItemLink.displayName = PREVIEW_SEARCH_ITEM_LINK_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchSuggestions
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_SUGGESTIONS_NAME = 'PreviewSearchSuggestions';
const PreviewSearchSuggestions = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, ...suggestionsProps } = props;
    return _jsx(Primitive.div, { role: "list", ...suggestionsProps, ref: forwardedRef });
});
PreviewSearchSuggestions.displayName = PREVIEW_SEARCH_SUGGESTIONS_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchSuggestionsGroup
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_SUGGESTIONS_GROUP_NAME = 'PreviewSearchSuggestionsGroup';
const [PreviewSearchSuggestionsGroupProvider, usePreviewSearchSuggestionsGroupContext] = createPreviewSearchContext(PREVIEW_SEARCH_NAME);
const PreviewSearchSuggestionsGroup = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, id, filterAttribute, ...suggestionsGroupProps } = props;
    const displayText = React.useRef(undefined);
    return (_jsx(PreviewSearchSuggestionsGroupProvider, { scope: __scopePreviewSearch, groupId: id, displayText: displayText, filterAttribute: filterAttribute, children: _jsx(Primitive.div, { role: "group", ...suggestionsGroupProps, ref: forwardedRef }) }));
});
PreviewSearchSuggestionsGroup.displayName = PREVIEW_SEARCH_SUGGESTIONS_GROUP_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchSuggestionItem
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_SUGGESTION_ITEM_NAME = 'PreviewSearchSuggestionItem';
function getSuggestionId(groupId, id) {
    return `suggestion-${groupId}-${id}`;
}
const PreviewSearchSuggestionItem = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, id, disabled, ...suggestionItemProps } = props;
    return (_jsx(FocusGroupItem, { asChild: true, children: _jsx(Primitive.button, { disabled: disabled, "data-disabled": disabled ? '' : undefined, ...suggestionItemProps, ref: forwardedRef }) }));
});
PreviewSearchSuggestionItem.displayName = PREVIEW_SEARCH_SUGGESTION_ITEM_NAME;
/* -------------------------------------------------------------------------------------------------
 * PreviewSearchSuggestionTrigger
 * ----------------------------------------------------------------------------------------------- */
const PREVIEW_SEARCH_SUGGESTION_TRIGGER = 'PreviewSearchSuggestionTrigger';
const PreviewSearchSuggestionTrigger = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, id, disabled } = props;
    const { setOpen } = usePreviewSearchContext(PREVIEW_SEARCH_SUGGESTION_TRIGGER, __scopePreviewSearch);
    const { groupId, filterAttribute } = usePreviewSearchSuggestionsGroupContext(PREVIEW_SEARCH_SUGGESTION_TRIGGER, __scopePreviewSearch);
    const suggestionId = getSuggestionId(groupId, id);
    const { activeItem, onTriggerEnter, onTriggerLeave } = useResultsContext(PREVIEW_SEARCH_SUGGESTION_TRIGGER, __scopePreviewSearch);
    const { id: currentSuggestionId } = activeItem || {};
    const active = suggestionId === currentSuggestionId;
    return (_jsx(PreviewSearchSuggestionItem, { "data-state": active ? 'active' : undefined, ...props, ref: forwardedRef, onPointerEnter: composeEventHandlers(props.onPointerEnter, whenMouse(() => {
            if (!disabled) {
                onTriggerEnter({
                    id: suggestionId,
                    value: id,
                    filterAttribute,
                });
            }
        })), onPointerLeave: composeEventHandlers(props.onPointerLeave, whenMouse(() => {
            if (!disabled) {
                onTriggerLeave();
            }
        })), onClick: composeEventHandlers(props.onClick, () => {
            setOpen(false);
        }) }));
});
PreviewSearchSuggestionTrigger.displayName = PREVIEW_SEARCH_SUGGESTION_TRIGGER;
/* ----------------------------------------------------------------------------------------------- */
const ARROW_KEYS = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'];
const FOCUS_GROUP_ITEM_NAME = 'FocusGroupItem';
const FocusGroupItem = React.forwardRef((props, forwardedRef) => {
    const { __scopePreviewSearch, ...groupProps } = props;
    const getItems = useFocusGroupCollection(__scopePreviewSearch);
    const context = usePreviewSearchContext(FOCUS_GROUP_ITEM_NAME, __scopePreviewSearch);
    return (_jsx(FocusGroupCollection.ItemSlot, { scope: __scopePreviewSearch, children: _jsx(Primitive.button, { role: "listitem", ...groupProps, tabIndex: 0, ref: forwardedRef, onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
                const isFocusNavigationKey = ['Home', 'End', ...ARROW_KEYS].includes(event.key);
                if (isFocusNavigationKey) {
                    let candidateNodes = getItems().map((item) => item.ref.current);
                    const prevItemKey = context.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
                    const prevKeys = [prevItemKey, 'ArrowUp', 'End'];
                    if (prevKeys.includes(event.key))
                        candidateNodes.reverse();
                    if (ARROW_KEYS.includes(event.key)) {
                        const currentIndex = candidateNodes.indexOf(event.currentTarget);
                        if (currentIndex < candidateNodes.length - 1) {
                            candidateNodes = candidateNodes.slice(currentIndex + 1);
                        }
                    }
                    /**
                     * Imperative focus during keydown is risky so we prevent React's batching updates
                     * to avoid potential bugs. See: https://github.com/facebook/react/issues/20332
                     */
                    setTimeout(() => focusFirst(candidateNodes));
                    // Prevent page scroll while navigating
                    event.preventDefault();
                }
            }) }) }));
});
function focusFirst(candidates) {
    const previouslyFocusedElement = document.activeElement;
    return candidates.some((candidate) => {
        // if focus is already where we want to go, we don't want to keep going through the candidates
        if (candidate === previouslyFocusedElement)
            return true;
        candidate.focus();
        return document.activeElement !== previouslyFocusedElement;
    });
}
function whenMouse(handler) {
    return (event) => (event.pointerType === 'mouse' ? handler(event) : undefined);
}
/* ----------------------------------------------------------------------------------------------- */
const Root = PreviewSearch;
const Input = PreviewSearchInput;
const Content = PreviewSearchContent;
const Results = PreviewSearchResults;
const Suggestions = PreviewSearchSuggestions;
const SuggestionsGroup = PreviewSearchSuggestionsGroup;
// const SuggestionsGroupHeader = PreviewSearchSuggestionsGroupHeader;
const SuggestionItem = PreviewSearchSuggestionItem;
const SuggestionTrigger = PreviewSearchSuggestionTrigger;
const Items = PreviewSearchItems;
const Item = PreviewSearchItem;
const ItemLink = PreviewSearchItemLink;
export { createPreviewSearchScope, 
//
Root, Input, Content, Results, Suggestions, SuggestionsGroup, 
// SuggestionsGroupHeader,
SuggestionItem, SuggestionTrigger, Items, Item, ItemLink, 
//
PreviewSearch, PreviewSearchInput, PreviewSearchContent, PreviewSearchSuggestions, PreviewSearchSuggestionsGroup, PreviewSearchSuggestionItem, PreviewSearchSuggestionTrigger, PreviewSearchResults, PreviewSearchItemLink, };
